/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

export const AuthContext = createContext(null);

function isTokenExpired(token) {
  try {
    const payload = token ? token.split('.')[1] : '';
    if (!payload) return false;
    const json = JSON.parse(atob(payload));
    if (!json || typeof json.exp !== 'number') return false;
    return Date.now() >= json.exp * 1000;
  } catch {
    return false;
  }
}

function clearSession() {
  try {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    // Left behind by the old marketplace admin's store switcher.
    localStorage.removeItem('activeStoreId');
  } catch {
    // Storage unavailable — nothing to clear.
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const storedUser = localStorage.getItem('user');
    try {
      localStorage.removeItem('activeStoreId');
    } catch {
      // ignore
    }

    if (token && isTokenExpired(token)) {
      clearSession();
    } else if (token && storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        clearSession();
      }
    }

    // Optional: verify session with backend to refresh roles/profile (disabled by default)
    const ENABLE_VERIFY = (import.meta.env?.VITE_ENABLE_AUTH_VERIFY ?? 'false') === 'true';
    const VERIFY_PATH = import.meta.env?.VITE_AUTH_VERIFY_PATH || '/api/auth/verify';
    const VERIFY_METHOD = (import.meta.env?.VITE_AUTH_VERIFY_METHOD || 'GET').toLowerCase();
    const verifySession = async () => {
      if (!token || !ENABLE_VERIFY) {
        setLoading(false);
        return;
      }
      try {
        const res = await api[VERIFY_METHOD](VERIFY_PATH);
        if (res?.data) {
          setUser(res.data);
          try {
            localStorage.setItem('user', JSON.stringify(res.data));
          } catch {
            // ignore
          }
        }
      } catch {
        // Let the response interceptor handle 401 (logout) if verification is enabled
      } finally {
        setLoading(false);
      }
    };
    verifySession();
  }, []);

  const login = (userData, token) => {
    localStorage.setItem('accessToken', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    clearSession();
    setUser(null);
    window.location.href = '/';
  };

  const value = {
    user,
    isAuthenticated: !!user,
    loading,
    login,
    logout,
  };

  // Don't render children until the initial token check is complete
  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
